import { useCallback, useEffect, useReducer } from "react";
import {
  ACTIVATION_CODE_MAX_FAILURES,
  clearActivationCodeAttemptState,
  getActivationCodeAttemptStorageKey,
  getActivationCodeRateLimitSnapshot,
  recordActivationCodeFailure,
  remainingCooldownSeconds,
} from "../utils/activation-code-attempt-storage";

export type UseActivationCodeRateLimitResult = {
  inCooldown: boolean;
  isBlocked: boolean;
  failures: number;
  attemptsRemaining: number;
  remainingSeconds: number;
  registerFailure: () => void;
  registerSuccess: () => void;
};

/**
 * Rate limit for activation code entry: max failures, then cooldown.
 * Subscribes to `storage` for cross-tab consistency and ticks once per second during cooldown.
 */
export function useActivationCodeRateLimit(
  scopeKey: string,
): UseActivationCodeRateLimitResult {
  const [, bump] = useReducer((n: number) => n + 1, 0);

  const storageKey = getActivationCodeAttemptStorageKey();

  const snapshot = getActivationCodeRateLimitSnapshot(scopeKey);
  const remainingSeconds = remainingCooldownSeconds(snapshot.cooldownUntil);

  const inCooldown = snapshot.inCooldown;
  const isBlocked = inCooldown;

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey) bump();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storageKey]);

  useEffect(() => {
    if (!inCooldown) return;
    const id = window.setInterval(() => bump(), 1000);
    return () => window.clearInterval(id);
  }, [inCooldown]);

  const registerFailure = useCallback(() => {
    recordActivationCodeFailure(scopeKey);
    bump();
  }, [scopeKey]);

  const registerSuccess = useCallback(() => {
    clearActivationCodeAttemptState(scopeKey);
    bump();
  }, [scopeKey]);

  return {
    inCooldown,
    isBlocked,
    failures: snapshot.failures,
    attemptsRemaining: snapshot.attemptsRemaining,
    remainingSeconds,
    registerFailure,
    registerSuccess,
  };
}

export { ACTIVATION_CODE_MAX_FAILURES };
